import { useEffect, useMemo, useState } from 'react';
import { CHAMPS_DE_TRACE, CONTACT_TYPES, FAMILLES, alignerSurLesJeux, champsNatifs, definitionsDeJeux, schemaDeFamille, fusionnerDeclarations, lireChampsDeclares, lireEcransDeclares } from '@taki/data-model';
import type { LectureInstance, TakiSdk } from '@taki/sdk';
import { demoRegistry } from './registry.js';
import type {
  ChoixDeConflit,
  DeclarationDApp,
  DeclarationsPort,
  FusionDeclaree,
  InstanceDApp,
  InstanceIllisible,
  ValidationDeclaree,
} from '@taki/ui';

/**
 * LE PORT DES DECLARATIONS DE L'HOTE : les apps instanciees par l'org, du
 * document Firestore `orgs/{orgId}/apps/{appId}` jusqu'au socle.
 *
 * MONTE ICI ET PAS DANS LE SHELL, pour la meme raison que le branding, les
 * modeles, les liens et l'acces : c'est l'HOTE qui a le SDK. Le socle ne
 * connait que la FORME (`DeclarationsPort`, @taki/ui) ; l'ecran generique de
 * `@taki/app-runtime` et le hub Parametres du shell lisent le meme port.
 *
 * TROIS DECISIONS VIVENT ICI :
 *
 *  1. L'OBSERVATION COMMENCE A LA CONNEXION, jamais avant. Contrairement au
 *     branding, ce document n'est pas public : les rules exigent un membre
 *     actif, et un abonnement lance sans compte ne rapporterait qu'un refus.
 *     Quand l'utilisateur change, on repart de zero : les instances d'une org
 *     ne se lisent jamais avec les droits d'un compte precedent.
 *  2. RIEN NE SE MONTE AVANT LA PREMIERE LECTURE (`pret`). Monter Contact sur
 *     son modele puis le remplacer par l'instance une seconde plus tard ferait
 *     sauter l'ecran sous les yeux de l'utilisateur ; l'hote attend donc `pret`
 *     comme il attend la fiche membre. Une fois pret, les changements suivants
 *     arrivent en direct par le snapshot.
 *  3. UNE PANNE D'OBSERVATION NE CASSE RIEN. Les apps tournent sur leur modele,
 *     le port porte l'erreur pour que Parametres la dise, et on previent en
 *     console. Une instance qui manque coute une personnalisation, jamais
 *     l'usage de l'app.
 */

/** Le contenu du port avant toute lecture, et apres une deconnexion. */
const RIEN: { instances: ReadonlyMap<string, InstanceDApp>; illisibles: readonly InstanceIllisible[] } = {
  instances: new Map(),
  illisibles: [],
};

/* LES TRACES (« Cree par », « Cree le »...) SONT DES NATIFS DU STUDIO, en lecture
   seule : le client peut les montrer dans une fiche ou une colonne, jamais les
   editer. Elles vivent hors de `champsNatifs`, qui est aussi le JUGE des
   ecritures natives (`validerChampNatif`), et c'est pour ca qu'on les ajoute ici. */
const FAMILLES_DU_STUDIO = FAMILLES.map((family) => ({
  family,
  natifs: [...champsNatifs(family), ...CHAMPS_DE_TRACE],
  typeRequis: schemaDeFamille(family).shape.type?.safeParse(undefined).success === false,
  ...(family === 'contacts' ? { types: CONTACT_TYPES } : {}),
}));

/**
 * Deux lectures rangees : les instances lisibles par id, et les documents
 * illisibles avec leur raison. Les types du SDK (valides par Zod) sont
 * assignes aux formes structurelles du socle : les deux compilent ensemble
 * ou pas du tout.
 */
function ranger(lectures: readonly LectureInstance[]): typeof RIEN {
  const instances = new Map<string, InstanceDApp>();
  const illisibles: InstanceIllisible[] = [];
  for (const lecture of lectures) {
    if (lecture.instance === null) illisibles.push({ appId: lecture.appId, raison: lecture.raison });
    else instances.set(lecture.appId, lecture.instance);
  }
  return { instances, illisibles };
}

/**
 * RELIRE UNE DECLARATION AVANT DE L'ECRIRE, par les MEMES lecteurs que l'ecran
 * generique (`lireChampsDeclares`, `lireEcransDeclares`) : une declaration
 * ecrite par un client traverse la meme porte que celle d'un agent.
 *
 * ELLE VIT ICI, DANS L'HOTE, et pas dans le shell : les lecteurs sont des
 * schemas Zod de `@taki/data-model`, que ni `@taki/ui` ni `@taki/shell` n'ont
 * dans leurs dependances. Le studio client du shell la demande donc au port,
 * exactement comme il demande `enregistrer`.
 *
 * UN SEUL ECRAN, ET C'EST UNE REGLE DU REGIME DECLARE : `resoudreDeclaration`
 * refuse une instance qui n'en porte pas exactement un, et l'ecrire quand meme
 * produirait un document que l'app ignorerait en silence. On le refuse ici,
 * avec la meme phrase.
 */
/**
 * UN JEU PARTAGE NE CASSE PAS L'AUTRE APP (`docs/socle/jeu-listes.md`). Les
 * jeux de la declaration valent pour toute app de l'org qui les montre : on
 * rejoue donc le montage de chacune, recalee sur eux, avant d'ecrire. Un champ
 * que Projets filtre et que Timesheet retirerait est refuse ici, avec le nom de
 * l'app qu'il casserait.
 */
function refusDesJeuxPartages(
  appId: string,
  declaration: DeclarationDApp,
  instances: ReadonlyMap<string, InstanceDApp>,
): string | null {
  const jeux = definitionsDeJeux(declaration);
  for (const [autre, entree] of Object.entries(demoRegistry)) {
    if (autre === appId || entree.declaration?.verifier === undefined) continue;
    const instance = instances.get(autre);
    const base = { champs: instance?.champs ?? entree.declaration.champs, ecrans: instance?.ecrans ?? entree.declaration.ecrans };
    const alignee = alignerSurLesJeux(base, jeux);
    if (alignee === base) continue;
    try {
      entree.declaration.verifier(alignee);
    } catch (cause) {
      const partages = [...definitionsDeJeux(alignee).keys()].filter((cle) => jeux.has(cle)).map((cle) => cle.split('::')[0]).join(', ');
      return `Ce jeu (${partages}) est aussi celui de ${entree.fiche.name ?? entree.fiche.menu.label}, qui ne le monterait plus : ${cause instanceof Error ? cause.message : String(cause)}`;
    }
  }
  return null;
}

function validerDeclaration(
  declaration: DeclarationDApp,
  appId?: string,
  instances: ReadonlyMap<string, InstanceDApp> = new Map(),
): ValidationDeclaree {
  try {
    const champs = lireChampsDeclares(declaration.champs, 'taki.fields.json (org)');
    const ecrans = lireEcransDeclares(declaration.ecrans, 'taki.screens.json (org)');
    if (ecrans.ecrans.length !== 1) {
      return {
        ok: false,
        raison: `La declaration porte ${String(ecrans.ecrans.length)} ecrans : une app en porte UN.`,
      };
    }
    if (appId !== undefined) demoRegistry[appId]?.declaration?.verifier?.(declaration);
    const refus = appId === undefined ? null : refusDesJeuxPartages(appId, declaration, instances);
    if (refus !== null) return { ok: false, raison: refus };
    return {
      ok: true,
      declaration: {
        champs,
        ecrans,
        ...(declaration.fiche === undefined ? {} : { fiche: declaration.fiche }),
      },
    };
  } catch (cause) {
    return { ok: false, raison: cause instanceof Error ? cause.message : String(cause) };
  }
}

/**
 * LA FUSION A TROIS VOIES, passee au socle telle quelle.
 *
 * `fusionnerDeclarations` est PURE et vit dans `@taki/data-model` : elle relit
 * son propre resultat, donc le `resultat` rendu ici est deja valide. La seule
 * chose faite en plus est de RETYPER sa sortie vers les formes structurelles du
 * socle : `fiche` y est un `Record<string, unknown>` (la fusion ne connait pas
 * la liste fermee), et le schema du SDK la refusera si elle porte autre chose.
 */
function fusionnerPourLeSocle(
  base: DeclarationDApp,
  modele: DeclarationDApp,
  instance: DeclarationDApp,
  choix: ChoixDeConflit = {},
): FusionDeclaree {
  const fusion = fusionnerDeclarations(base, modele, instance, choix);
  return {
    resultat:
      fusion.resultat === null
        ? null
        : ({
            champs: fusion.resultat.champs,
            ecrans: fusion.resultat.ecrans,
            ...(fusion.resultat.fiche === undefined ? {} : { fiche: fusion.resultat.fiche }),
          } as DeclarationDApp),
    changements: fusion.changements,
    conflits: fusion.conflits,
    refus: fusion.refus,
  };
}

/**
 * Le port pret a etre passe a `DeclarationsProvider`.
 *
 * @param email L'utilisateur connecte, `null` sans compte : c'est la clef de
 * l'observation, comme pour la fiche membre.
 * @param peutModifier Le MEME droit que la gestion des membres (owner ou admin
 * actif) : les rules exigent exactement le meme.
 */
export function useDeclarationsDeLHote(
  sdk: TakiSdk,
  email: string | null,
  peutModifier: boolean,
): DeclarationsPort {
  const [lu, setLu] = useState(RIEN);
  const [pret, setPret] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (email === null) {
      setLu(RIEN);
      setPret(false);
      setErreur(null);
      return;
    }
    let vivant = true;
    setPret(false);
    setErreur(null);
    let arreter: (() => void) | undefined;
    try {
      arreter = sdk.appsInstanciees.observer(
        (lectures) => {
          if (!vivant) return;
          setLu(ranger(lectures));
          setErreur(null);
          setPret(true);
        },
        (cause: unknown) => {
          if (!vivant) return;
          console.warn('Apps instanciees : lecture impossible, les apps tournent sur leur modele.', cause);
          setErreur(
            "Impossible de lire les personnalisations de cette organisation pour le moment : les apps tournent sur leur modele.",
          );
          setPret(true);
        },
      );
    } catch (cause) {
      console.warn('Apps instanciees : observation impossible.', cause);
      setErreur(
        "Impossible de lire les personnalisations de cette organisation pour le moment : les apps tournent sur leur modele.",
      );
      setPret(true);
    }
    return () => {
      vivant = false;
      arreter?.();
    };
  }, [sdk, email]);

  return useMemo<DeclarationsPort>(
    () => ({
      familles: FAMILLES_DU_STUDIO,
      pret,
      instances: lu.instances,
      illisibles: lu.illisibles,
      erreur,
      peutModifier,
      optionsChamp: (input) => sdk.appsInstanciees.optionsChamp(input),
      enregistrer: (appId, instance) => sdk.appsInstanciees.enregistrer(appId, instance),
      reinitialiser: (appId) => sdk.appsInstanciees.reinitialiser(appId),
      sequences: sdk.sequences,
      branchementsDeBoutons: async (appId) => {
        const flux = (sdk as unknown as {
          flux?: { listerBranchementsDeBoutons?: (appId?: string) => Promise<Record<string, { fluxId: string; nom: string; sortExterne?: boolean }[]>> };
        }).flux;
        return flux?.listerBranchementsDeBoutons === undefined ? null : flux.listerBranchementsDeBoutons.call(flux, appId);
      },
      valider: (declaration, appId) => validerDeclaration(declaration, appId, lu.instances),
      fusionner: fusionnerPourLeSocle,
    }),
    [pret, lu, erreur, peutModifier, sdk],
  );
}
